import { logoUrl } from '../../api/branding.api.js';
import { readableOn } from '../../lib/readableInk.js';
import { defaultBranding, useBrandingStore } from '../../stores/brandingStore.js';

interface Props {
  size?: 'sm' | 'md' | 'lg';
  fallback?: 'monogram' | 'text' | 'none';
}

const SIZE_CLASS = {
  sm: 'h-7',
  md: 'h-10',
  lg: 'h-16',
};

export function BrandLogo({ size = 'md', fallback = 'monogram' }: Props): JSX.Element {
  const branding = useBrandingStore((s) => s.branding);
  const version = useBrandingStore((s) => s.version);
  const appName = branding?.appName ?? 'Attendance';

  if (branding?.logoUrl) {
    return (
      <img
        src={logoUrl(version || undefined)}
        alt={appName}
        className={`${SIZE_CLASS[size]} w-auto object-contain`}
      />
    );
  }

  if (fallback === 'text') {
    /**
     * The wordmark is `--primary`, NOT the tenant's `--brand-primary`.
     *
     * This is text on the page, so it is measured against `--background`, and
     * `--background` inverts between themes while a tenant hex is one value for
     * both. index.css can express that split for its own seed — it overrides
     * `--brand-primary` to #c9beff in dark precisely because the light violet
     * read 3.52:1 on the dark page — but the store writes the tenant colour as
     * an INLINE property on `<html>`, which outranks both `:root` and `.dark`,
     * so once a tenant saves a colour that override can never apply again.
     *
     * There is no way to derive a page-legible tone from a single hex without
     * knowing the theme, and the fill-luminance trick used by the monogram
     * below does not transfer: that one owns its background, this one does not.
     * So until per-tenant M3 re-seeding lands (index.css, "Per-tenant M3
     * re-seeding is a follow-up") the wordmark uses the theme-aware token that
     * tracks the seed brand: 6.54:1 light, 10.85:1 dark.
     */
    // The wordmark is Archivo 700 with the display steps' own tight tracking —
    // `Hadir Dashboard.dc.html` sets the rail's wordmark at 16px/700/−0.2px, and
    // the display steps are where that pairing lives. `text-2xl`/`text-base`/
    // `text-sm` were stock Tailwind sizes, i.e. Instrument Sans at default
    // tracking: the product's own name set in the UI-text face.
    return (
      <span
        className={`font-bold text-primary ${
          size === 'lg' ? 'text-h2' : size === 'md' ? 'text-h3' : 'text-copy font-display'
        }`}
      >
        {appName}
      </span>
    );
  }

  if (fallback === 'monogram') {
    const letter = appName.trim().charAt(0).toUpperCase() || 'A';
    // The mock's rail tile is a 34px square at `border-radius: 10px` with the
    // letter in Archivo 700 at half the tile — see `Hadir Dashboard.dc.html`.
    // The three sizes here keep that ratio; the corner moves below.
    const dim =
      size === 'lg'
        ? 'h-12 w-12 text-h2'
        : size === 'md'
          ? // 34px, the handoff's rail tile (Hadir Dashboard.dc.html:34).
            // `h-9` is 36 — two over, and visible against a 34px wordmark row.
            'h-[2.125rem] w-[2.125rem] text-h3'
          : 'h-7 w-7 text-copy';
    /**
     * Fill and ink are resolved from the SAME hex, in JS, rather than from
     * `var(--brand-primary)` plus a token.
     *
     * The tile is one of the two places the brand is allowed to appear on the
     * rail, so the fill has to stay the tenant's colour — but the letter used
     * to be `text-primary-foreground`, and that token flips with the theme
     * while the fill does not. On the live tenant colour #0066FF the letter
     * measured 4.83:1 light / 2.73:1 dark; on a pale brand (a yellow, or the
     * app's own dark-mode #c9beff) it goes to 1.4:1 in light. Any hex breaks in
     * one theme or the other, so the pairing was unsafe by construction, not
     * unlucky in its seed.
     *
     * `readableOn` derives the letter from the fill's own luminance, which
     * removes the theme from the pair entirely: #0066FF is now 4.83:1 in both,
     * and the pale brands that used to bottom out at 1.4:1 read 10:1+.
     */
    const fill = branding?.primaryColor ?? defaultBranding.primaryColor;
    return (
      <span
        // `rounded-control` (10px), not `rounded-card`. The handoff's rail tile
        // is 10px, and `--radius-card` moved 8px → 12px in the warm-paper
        // migration — so leaving this class alone would have quietly grown the
        // corner on a 28px tile until it read as a lozenge. A small filled
        // square is the control/chip end of the ramp, not the inner-card end.
        className={`inline-flex items-center justify-center rounded-control font-display font-bold ${dim}`}
        style={{ backgroundColor: fill, color: readableOn(fill) }}
      >
        {letter}
      </span>
    );
  }

  return <span />;
}
